import { call, put, select } from 'typed-redux-saga';

import logger from '@/core/observability/logger';
import { getAppSetting } from '@/core/settings/appSettings';
import { getOrCreateVisitorId } from '@/core/tracking/utils/visitorId';
import { authSelector } from '@/customerManagement/customerManagement.selectors';
import { AlgoliaSearchInsights } from '@/productDiscovery/algolia/searchInsights/AlgoliaSearchInsights';
import type { ProductHistory } from '@/productDiscovery/Recommendation/modules/ProductHistory/ProductHistory.model';
import { getLocalStorageProductHistory } from '@/productDiscovery/Recommendation/modules/ProductHistory/utils/index';
import { modelIdsForRecommendationSelector } from '@/productDiscovery/Recommendation/modules/ProductHistoryRecommendation/hooks/useRecommendationsIds';

import type { ProductsSuggestionSearchResult } from '../../productSuggestions/interfaces/algoliaResponse';
import {
  AlgoliaQueriesIndexes,
  MIN_LAST_SEEN_PRODUCTS_WITH_SIMILARITY,
  NB_LAST_SEEN_PRODUCTS_WITH_SIMILARITY,
  PRODUCTS_IN_SEARCHBAR_RECO_ENGINE,
} from '../constants';
import {
  getEmptySearchSuggestionsFailed,
  getEmptySearchSuggestionsSuccess,
  updateSuggestionsHistory,
} from '../HeaderLeonidas.searchBar.actions';
import { suggestionsEmptySearchSelector } from '../hooks/useSuggestionsEmptySearch';
import type {
  QuerySuggestionSearchResults,
  SuggestionsSearchResults,
} from '../interfaces/algolia';
import {
  createSuggestionsSearchClientInstance,
  searchMultiple,
} from '../services/algolia/proxy';
import { buildTopSuggestionsSearchParameters } from '../services/algolia/searchParameters';
import { processStaticProductsResult } from '../utils/processStaticProductsResult';
import { randomizeSuggestions } from '../utils/randomizeSuggestions';

function extractAdditionalProductsSuggestions({
  searchResults,
  topSaleProducts,
  hasStaticSuggestions,
  hasLastSeenProducts,
  hasSimilarityProducts,
}: {
  searchResults: SuggestionsSearchResults;
  topSaleProducts: ProductsSuggestionSearchResult;
  hasStaticSuggestions: boolean;
  hasLastSeenProducts: boolean;
  hasSimilarityProducts: boolean;
}): Record<string, ProductsSuggestionSearchResult> {
  let currentIndex = Math.max(
    ...Object.values(AlgoliaQueriesIndexes.emptySearch),
  );
  let staticProducts;
  let lastSeenProducts;
  let similarityProducts;

  if (hasStaticSuggestions) {
    currentIndex += 1;
    staticProducts = searchResults[currentIndex];
  }

  if (hasLastSeenProducts) {
    currentIndex += 1;
    lastSeenProducts = searchResults[currentIndex];
  }

  if (hasSimilarityProducts) {
    currentIndex += 1;
    similarityProducts = searchResults[currentIndex];
  }

  const topProducts = hasStaticSuggestions
    ? processStaticProductsResult(staticProducts, topSaleProducts)
    : topSaleProducts;

  return {
    topProducts,
    lastSeenProducts,
    similarityProducts,
  };
}

export function* updateSuggestionsHistorySaga() {
  const emptySearch = yield* select(suggestionsEmptySearchSelector);
  const topQueries = emptySearch?.queries;

  if (topQueries?.items?.length) {
    yield put(updateSuggestionsHistory());
    return;
  }
}

export function* getEmptySearchSuggestions() {
  try {
    const { userId } = yield* select(authSelector);
    const visitorId = yield* call(getOrCreateVisitorId);
    const userToken = userId
      ? yield* call(AlgoliaSearchInsights.calculateUserIdHash, String(userId))
      : visitorId;

    const client = yield* call(createSuggestionsSearchClientInstance);

    // Static suggestions
    let staticSuggestionsIds: string[] = [];

    try {
      staticSuggestionsIds = JSON.parse(
        getAppSetting('SEARCHBAR_EMPTYSEARCH_SUGGESTIONS'),
      ).product;
    } catch (error) {
      logger.error(error);
    }

    // Last seen products
    let lastSeenProductsIds: string[] = [];

    try {
      const productsInHistory: ProductHistory = yield call(
        getLocalStorageProductHistory,
      );

      if (productsInHistory.length >= MIN_LAST_SEEN_PRODUCTS_WITH_SIMILARITY) {
        lastSeenProductsIds = productsInHistory
          .slice(0, NB_LAST_SEEN_PRODUCTS_WITH_SIMILARITY)
          .map(({ productId }) => `${productId}`);
      }
    } catch (error) {
      logger.error(error);
    }

    // Similarity products
    let similarityProductsIds: string[] = [];

    if (lastSeenProductsIds.length >= MIN_LAST_SEEN_PRODUCTS_WITH_SIMILARITY) {
      similarityProductsIds = yield select((state) =>
        modelIdsForRecommendationSelector(
          state,
          PRODUCTS_IN_SEARCHBAR_RECO_ENGINE,
        ),
      );
    }

    // Get top suggestions
    const topSuggestionsResults: SuggestionsSearchResults = yield call(
      searchMultiple,
      client,
      buildTopSuggestionsSearchParameters({
        staticSuggestions: staticSuggestionsIds,
        lastSeenProducts: lastSeenProductsIds,
        similarityProducts: similarityProductsIds,
      }),
      userToken,
    );

    const {
      QUERY_SUGGESTIONS_INDEX,
      CATEGORY_SUGGESTIONS_INDEX,
      BRAND_SUGGESTIONS_INDEX,
      TOP_SALE_PRODUCTS_SUGGESTIONS_INDEX,
    } = AlgoliaQueriesIndexes.emptySearch;

    const topQuery = topSuggestionsResults[QUERY_SUGGESTIONS_INDEX];
    const topCategory = topSuggestionsResults[CATEGORY_SUGGESTIONS_INDEX];
    const topBrand = topSuggestionsResults[BRAND_SUGGESTIONS_INDEX];
    const topSaleProducts =
      topSuggestionsResults[TOP_SALE_PRODUCTS_SUGGESTIONS_INDEX];

    const randomTopQueries = randomizeSuggestions(topQuery);

    const hasStaticSuggestions = staticSuggestionsIds.length > 0;
    const hasLastSeenProducts = lastSeenProductsIds.length > 0;
    const hasSimilarityProducts = similarityProductsIds.length > 0;

    const { topProducts, lastSeenProducts, similarityProducts } =
      extractAdditionalProductsSuggestions({
        searchResults: topSuggestionsResults,
        topSaleProducts,
        hasStaticSuggestions,
        hasLastSeenProducts,
        hasSimilarityProducts,
      });

    const shouldUseLastSeenProducts =
      lastSeenProducts?.hits?.length >= MIN_LAST_SEEN_PRODUCTS_WITH_SIMILARITY;

    const products = shouldUseLastSeenProducts ? lastSeenProducts : topProducts;

    yield put(
      getEmptySearchSuggestionsSuccess(
        {
          queries: randomTopQueries as QuerySuggestionSearchResults,
          categories: topCategory,
          brands: topBrand,
          products,
          similarityProducts,
        },
        hasStaticSuggestions,
        shouldUseLastSeenProducts,
      ),
    );
  } catch (error) {
    yield put(getEmptySearchSuggestionsFailed(error as Error));
  }
}
